import { CheckCheck, CircleHelp, FileText, HandCoins, Inbox, ListChecks, Sparkles } from 'lucide-react';
import { formatMoney, type TodayView } from '../api';
import { AgingBar, DeskSection, EmptyNote, formatDay, formatLongDay, formatMoment, useDeskData } from '../components/desk';

type Currency = TodayView['kpis']['currencies'][number];
/** The fields one row of tiles shows: a single currency, or every currency converted into the reporting currency. */
type KpiRow = Pick<Currency, 'currency' | 'openCount' | 'overdueCount' | 'openAR' | 'overdueAR' | 'overduePercent' | 'dso'>
  & { expected: Pick<Currency['expected'], 'total' | 'fromDueDates' | 'fromPromises'> };
const kindIcons = { approval: <CheckCheck size={16} aria-hidden />, reply: <Inbox size={16} aria-hidden />,
  promise: <HandCoins size={16} aria-hidden />, task: <ListChecks size={16} aria-hidden /> };
const kindLabels = { approval: 'Approve email', reply: 'Review reply', promise: 'Decide on promise', task: 'Task' };

function Tip({ text }: { text: string }) {
  return <span className="kpi-tip" tabIndex={0} aria-label={text}>
    <CircleHelp size={13} aria-hidden /><span role="tooltip">{text}</span></span>;
}

function CurrencyKpis({ row, view }: { row: KpiRow; view: TodayView }) {
  const money = (minor: string) => formatMoney(minor, row.currency);
  const dso = row.dso;
  return <div className="kpi-row" role="group" aria-label={`${row.currency} receivables`}>
    <span className="kpi-currency">{row.currency}</span>
    <div className="kpi"><span>Open AR</span><strong>{money(row.openAR.minor)}</strong>
      <small>{row.openCount} open {row.openCount === 1 ? 'invoice' : 'invoices'}</small></div>
    <div className="kpi"><span>Overdue</span><strong>{money(row.overdueAR.minor)}</strong>
      <small>{row.overduePercent === null ? 'No open AR' : `${row.overduePercent}% of open AR`}, {row.overdueCount} past due</small></div>
    <div className="kpi"><span>DSO <Tip text={`${dso.formula}. ${money(dso.openAR)} ÷ ${money(dso.creditSales)} × ${dso.days}, invoiced
      ${formatDay(view.kpis.dsoWindow.from, view.asOf)} to ${formatDay(view.kpis.dsoWindow.to, view.asOf)}.`} /></span>
      <strong>{dso.value === null ? '–' : <>{dso.value}<em> days</em></>}</strong>
      <small>{dso.unavailableReason ?? `Trailing ${dso.days} days`}</small></div>
    <div className="kpi"><span>Expected in 7 days <Tip text={`Open invoices due ${formatDay(view.kpis.horizon.from)} to
      ${formatDay(view.kpis.horizon.to)}, plus active promises to pay by ${formatDay(view.kpis.horizon.to)}. A promise replaces
      its invoices' due dates; disputed and already overdue invoices are not counted.`} /></span>
      <strong>{money(row.expected.total.minor)}</strong>
      <small>{row.expected.total.minor === '0' ? 'Nothing due or promised this week'
        : `${money(row.expected.fromDueDates)} due, ${money(row.expected.fromPromises)} promised`}</small></div>
  </div>;
}

export function Today() {
  const { data: view, error } = useDeskData<TodayView>('/app-api/today');
  const unknown = view?.kpis.unknownBalanceIds.length ?? 0;
  // A currency with no stored rate into the reporting currency is never dropped: it keeps its own row.
  const separate = view ? view.kpis.currencies.filter(row => view.reporting.missing.includes(row.currency)) : [];
  const waiting = view ? view.counts.approvals + view.counts.replies + view.counts.promises + view.counts.tasks : 0;
  return <main id="main-content" className="queue-page desk-page">
    <header className="page-heading"><div><span className="desk-date">{view ? formatLongDay(view.asOf) : 'Loading'}</span>
      <h1>Today<span className="heading-dot" aria-hidden="true">.</span></h1>
      <p>{view ? waiting ? `${view.persona} prepared ${waiting} ${waiting === 1 ? 'item' : 'items'} for a decision.`
        : `Nothing is waiting on you. ${view.persona} will prepare the next reminders when they come due.`
        : 'Reading the ledger…'}</p></div>
    </header>
    {error && <div className="alert error" role="alert">{error}</div>}
    {view && <>
      <section className="kpi-board" aria-label="Receivables at a glance">
        {view.kpis.currencies.length ? <>
          {view.reporting.included.length > 0 && <CurrencyKpis row={view.reporting} view={view} />}
          {separate.map(row => <CurrencyKpis key={row.currency} row={row} view={view} />)}
        </> : <EmptyNote icon={<FileText size={26} aria-hidden />} title="No invoices yet">
            Connect a ledger in Integrations to see receivables.</EmptyNote>}
        <div className="kpi-waiting" role="group" aria-label="Awaiting a decision">
          <span>Awaiting you</span>
          <a href="#approvals"><strong>{view.counts.approvals}</strong>
            {view.counts.approvals === 1 ? 'email to approve' : 'emails to approve'}</a>
          <a href="#inbox"><strong>{view.counts.replies}</strong>{view.counts.replies === 1 ? 'reply to review' : 'replies to review'}</a>
          <span><strong>{view.counts.promises + view.counts.tasks}</strong>promises and tasks</span>
        </div>
      </section>
      {view.reporting.note && <p className="desk-footnote">Totals in {view.reporting.currency}; {view.reporting.note}.</p>}
      {separate.length > 0 && <p className="desk-footnote">{separate.map(r => r.currency).join(', ')}: no
        {` ${view.reporting.currency}`} rate is stored, so {separate.length === 1 ? 'it is' : 'they are'} shown separately
        and not included in the {view.reporting.currency} totals.</p>}
      {view.kpis.currencies.length > 1 && <details className="kpi-breakdown">
        <summary>By currency</summary>
        {view.kpis.currencies.map(row => <CurrencyKpis key={row.currency} row={row} view={view} />)}
      </details>}
      {unknown > 0 && <p className="desk-footnote">{unknown === 1 ? 'One open invoice has an unknown balance and is'
        : `${unknown} open invoices have unknown balances and are`} left out of these totals.</p>}
      {view.kpis.currencies.length > 0 && <DeskSection title="Aging" className="aging-section"
        aside={<small className="muted">{view.reporting.note ? `In ${view.reporting.currency}; ${view.reporting.note}.`
          : `In ${view.reporting.currency}.`}</small>}>
        {view.reporting.included.length > 0
          && <AgingBar currency={view.reporting.currency} buckets={view.reporting.aging} />}
        {separate.map(row => <AgingBar key={row.currency} currency={row.currency} buckets={row.aging} />)}
      </DeskSection>}
      <div className="desk-columns">
        <DeskSection title={`${view.persona} prepared`} aside={<small className="muted">{view.prepared.length} waiting</small>}>
          {view.prepared.length ? <ol className="prepared-list">{view.prepared.map(item => <li key={item.id}>
            <a href={item.href}><span className={`prepared-icon ${item.kind}`}>{kindIcons[item.kind]}</span>
              <span className="prepared-body"><small>{kindLabels[item.kind]} for {item.customerName}</small><strong>{item.title}</strong>
                <span>{item.detail}</span></span>
              {item.amount && <span className="prepared-amount">{formatMoney(item.amount.minor, item.amount.currency)}</span>}</a>
          </li>)}</ol> : <EmptyNote icon={<Sparkles size={24} aria-hidden />} title="You’re caught up">
            Drafts, replies and broken promises that need a person show up here.</EmptyNote>}
        </DeskSection>
        <DeskSection title="Recent activity">
          {view.activity.length ? <ol className="activity-list">{view.activity.map(a => <li key={a.id}>
            <span className={`activity-dot ${a.actorKind}`} aria-hidden />
            <p>{a.customerId ? <>{a.summary.slice(0, a.summary.length - (a.customerName?.length ?? 0))}
              <a href={`#customers/${encodeURIComponent(a.customerId)}`}>{a.customerName}</a></> : a.summary}</p>
            <time dateTime={a.at}>{formatMoment(a.at, view.asOf)}</time></li>)}</ol>
            : <EmptyNote icon={<ListChecks size={24} aria-hidden />} title="No activity yet">Approvals, replies, payments and syncs are listed here as they happen.</EmptyNote>}
        </DeskSection>
      </div>
    </>}
  </main>;
}
